import React, { useContext, useState, useEffect } from "react";
import {
  Typography,
  IconButton,
  useMediaQuery,
  TextField,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ListIcon from '@mui/icons-material/List';
import { useLocation, useNavigate} from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import CancelIcon from "@mui/icons-material/Cancel";
import { COLORS, fontFamilyAll } from "../../assets/commonComponents/COLORS";
import { KnowledgeBaseContext } from "../../components/knowledgeBaseContext/KnowledgeBaseContext";

/* -------------------- Debounce Hook (in same file) -------------------- */
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

/* -------------------- Styled Components -------------------- */
const linkStyle = {
  textDecoration: "none",
  color: "white",
  "&:hover": {
    color: "white",
    textDecoration: "none",
  },
};

/* -------------------- Main Component -------------------- */
const NavbarWithSearch = () => {
  const { searchTerm, setSearchTerm, searchString, setMobileOpensideBar, setSearchString,knowledgebase } =
    useContext(KnowledgeBaseContext);
  const [inputValue, setInputValue] = useState(searchString || "");
  const debouncedSearch = useDebounce(inputValue, 600);
  const [loading, setLoading] = useState(false);

  const isMobile = useMediaQuery("(max-width: 900px)");
  const isSmall = useMediaQuery("(max-width: 600px)");
  const location = useLocation();

  const navigate = useNavigate();

  /* 🔹 Non-document path: debounce search handling */
  useEffect(() => {
    if (!location.pathname.includes("/document")) {
      if (debouncedSearch.trim() !== "") {
        setLoading(true);
        setSearchString(debouncedSearch);

        // simulate async (API call or filter)
        setTimeout(() => setLoading(false), 500);
      } else {
        setSearchString("");
        knowledgebase();
      }
    }
  }, [debouncedSearch, location.pathname, setSearchString]);

  const handleClearSearch = () => {
    if (location.pathname.includes("/document")) {
      setSearchTerm("");
    } else {
      setInputValue("");
      setSearchString("");
      knowledgebase();
    }
  };


  return (
    <>

      <Box sx={{ backgroundColor: COLORS.secondaryColor }}>
        {location.pathname.includes("/article") ? (
          <Box
            sx={{
              position: "fixed",
              top: "75px",
              left: 0,
              right: 0,
              backgroundColor: COLORS.secondaryColor,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              py: 1,
              zIndex: 1100,
              px: 2,
              gap: 1,
            }}
          >
            {/* Back button */}
            <IconButton
              aria-label="go back"
              onClick={() => {
                navigate("/knowledgebase")
                setSearchString(decodeURIComponent(location.pathname.split("/")[3]))
                setInputValue(decodeURIComponent(location.pathname.split("/")[3]))
              }}
              sx={{ color: "white" }}
            >
              <ArrowBackIcon />
            </IconButton>

            {/* Category name */}
            <Typography
              variant="h6"
              sx={{
                color: "white",
                fontFamily: fontFamilyAll,
                fontSize: "16px",
                fontWeight: 700,
              }}
            >
              {"Back to Search"}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              position: "fixed",
              top: "75px",
              left: 0,
              right: 0,
              backgroundColor: COLORS.secondaryColor,
              display: "flex",
              justifyContent:
                location.pathname.includes("/document") ? "flex-start" : "center",
              alignItems: "center",
              py: 1,
              zIndex: 1100,
              px: 2,
              gap: 1,
            }}
          >
            {location.pathname.includes("/document") && (
              <>
                {
                  isMobile &&
                  <IconButton
                    aria-label="open drawer"
                    edge="end"
                    onClick={() => setMobileOpensideBar(true)}
                    sx={{ ...linkStyle }}
                  >
                    <ListIcon />
                  </IconButton>
                }
                <IconButton aria-label="home" component="a" href={`/knowledgebase`} sx={{ ...linkStyle }}>
                  <HomeIcon />
                </IconButton>
              </>
            )}
            {
              (!isMobile && location.pathname.includes("/document") || (location.pathname == "/knowledgebase")) &&

              <TextField
                placeholder="Search here..."
                variant="outlined"
                value={location.pathname.includes("/document") ? searchTerm : inputValue}
                onChange={(e) =>
                  location.pathname.includes("/document")
                    ? setSearchTerm(e.target.value)
                    : setInputValue(e.target.value)
                }
                InputProps={{
                  endAdornment: (location.pathname.includes("/document")
                    ? searchTerm
                    : inputValue) && (
                      <InputAdornment position="end">
                        {loading && !location.pathname.includes("/document") ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton size="small" onClick={handleClearSearch}>
                           <CancelIcon/>
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                }}
                sx={{
                  width:
                    location.pathname.includes("/document")
                      ? isSmall
                        ? "100%"
                        : "220px"
                      : isSmall
                        ? "100%"
                        : "700px",
                  bgcolor: "#fff",
                  borderRadius: "50px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    fontFamily: fontFamilyAll,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: COLORS.secondaryColor },
                    "&.Mui-focused fieldset": { borderColor: COLORS.secondaryColor },
                  },
                  "& input": {
                    padding: isSmall ? "4px 14px" : "6px 20px",
                  },
                }}
              />
            }
          </Box>
        )}
      </Box>

    </>
  );
};

export default NavbarWithSearch;
